import React, { useState, forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Typography,
  Stack,
  Box,
  TableRow,
  TableCell,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  Checkbox,
  Card,
} from '@mui/material';
import Iconify from '../common/iconify.component';
import SnackbarInfo from '../common/snackbar-info.component';
import { CDN77_STATUS } from '../../config/const.config';
import { LISTING_COLUMNS } from '../../config/module-configs/hls-transcoder.config';
import { transcodeFTPFiles } from '../../services/ftp.service';
import { transcodeS3Files } from '../../services/hls-transcoder.service';
import { formatTimestamp } from '../../utils/datetime.util';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const HLSTranscoderFilesOverwrite = ({
  title,
  onCancel,
  rowsToOverwrite,
  ftpServerId,
  bucketId,
}) => {
  const [options, setOptions] = useState({
    rows: rowsToOverwrite,
    selectedRows: [],
    selectAllRows: false,
    columns: LISTING_COLUMNS,
  });
  const [processing, setProcessing] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const selectedRowsSet = useMemo(
    () => new Set(options.selectedRows.map((item) => item.id)),
    [options.selectedRows]
  );
  const filteredRowsList = useMemo(
    () => options.rows.filter((item) => item.status === CDN77_STATUS.NEW),
    [options.rows]
  );

  const handleStartTranscoding = async () => {
    setProcessing(true);
    let queuedCnt = 0;

    for (let i = 0; i < options.selectedRows.length; i += 1) {
      try {
        setSnackbarInfo({ ...snackbarInfo, show: false });
        setOptions((prev) => ({
          ...prev,
          rows: prev.rows.map((item) => ({
            ...item,
            isLoading: options.selectedRows[i].id === item.id,
          })),
        }));

        const transcodeFilePayload = {
          file: options.selectedRows[i],
          overwriteConfirmation: true,
        };
        let transcodeResponse = {};
        if (ftpServerId) {
          transcodeResponse = await transcodeFTPFiles(
            ftpServerId,
            transcodeFilePayload
          );
        } else if (bucketId) {
          transcodeResponse = await transcodeS3Files(
            bucketId,
            transcodeFilePayload
          );
        }
        if (transcodeResponse?.data) {
          queuedCnt += 1;
          const successCnt = queuedCnt;

          setOptions((prev) => ({
            ...prev,
            rows: prev.rows.map((item) =>
              item.id === transcodeResponse.data.id
                ? { ...transcodeResponse.data, isLoading: false }
                : item
            ),
            selectedRows: prev.selectedRows.filter(
              (item) => item.id !== options.selectedRows[i].id
            ),
            selectAllRows: false,
          }));

          setTimeout(() => {
            setSnackbarInfo({
              ...snackbarInfo,
              show: true,
              type: 'info',
              message: `File ${successCnt} / ${options.selectedRows.length} added to the transcoding queue.`,
            });
          }, 50);
        }
      } catch (err) {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setProcessing(false);
      }
    }

    onCancel();
    setProcessing(false);
  };

  const handleRowSelection = (r) => {
    const finalSelectedRows = selectedRowsSet.has(r.id)
      ? options.selectedRows.filter((row) => row.id !== r.id)
      : [...options.selectedRows, r];

    if (finalSelectedRows.length === filteredRowsList.length) {
      setOptions({
        ...options,
        selectedRows: finalSelectedRows,
        selectAllRows: true,
      });
    } else {
      setOptions({
        ...options,
        selectedRows: finalSelectedRows,
        selectAllRows: false,
      });
    }
  };

  const handleSelectAllChange = () => {
    const finalSelectedRows = !options.selectAllRows ? filteredRowsList : [];
    setOptions({
      ...options,
      selectedRows: finalSelectedRows,
      selectAllRows: !options.selectAllRows,
    });
  };

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="add-dialog-title">{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
        disabled={processing}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="add-dialog-description">
        <Box flex={1}>
          <Box pb={1}>
            <Typography variant="subtitle1" color="secondary" fontSize={11}>
              Following files seems to be already available at the encoder
              destination. If you would like to re-process them and overwrite
              the existing folder, then please select those items and click on
              confirm overwrite button to proceed.
            </Typography>
          </Box>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center">
              <Checkbox
                checked={options.selectAllRows}
                onChange={() => handleSelectAllChange()}
                disabled={
                  options.rows.length === 0 ||
                  processing ||
                  filteredRowsList.length === 0
                }
                sx={{ pl: 0 }}
              />
              <Typography variant="body2" color="text.secondary">
                Select All
              </Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              gap={1}
            >
              <Button
                type="button"
                color="primary"
                variant="contained"
                startIcon={processing && <Iconify icon="eos-icons:loading" />}
                onClick={() => handleStartTranscoding()}
                disabled={
                  options.rows.length === 0 ||
                  options.selectedRows.length === 0 ||
                  processing
                }
              >
                {!processing ? 'Confirm Overwrite' : 'Overwriting...'}
              </Button>
            </Box>
          </Stack>

          {processing && (
            <Box py={1}>
              <Typography variant="subtitle1" color="primary" fontSize={11}>
                We&apos;re processing the request. Please do not close the
                window and do not leave this page.
              </Typography>
            </Box>
          )}

          <Card sx={{ my: 0.5 }}>
            <TableContainer variant="outlined" square>
              <Table size="small" aria-label="a dense table">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" />
                    {options.columns.map((c) => (
                      <TableCell
                        key={`col-${c.id}`}
                        align={c.align}
                        width={c.width}
                        sx={{
                          cursor: c.sortable ? 'pointer' : 'inherit',
                        }}
                      >
                        <Stack direction="row" justifyContent={c.align}>
                          {c.label}
                        </Stack>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {options.rows.map((r) => (
                    <TableRow key={`ftp-files-${r.id}`}>
                      <TableCell align="center">
                        {r.status === CDN77_STATUS.NEW && (
                          <Checkbox
                            checked={selectedRowsSet.has(r.id)}
                            onChange={() => handleRowSelection(r)}
                            disabled={processing}
                            sx={{ p: 0 }}
                          />
                        )}
                      </TableCell>
                      {options.columns.map((c) => (
                        <TableCell
                          key={`data-col-${c.id}-${r[c.dataKey]}`}
                          align={c.align}
                          width={c.width}
                          variant="string"
                        >
                          {c.dataKey === 'status' &&
                            (r.isLoading ? (
                              <Iconify icon="line-md:loading-loop" />
                            ) : (
                              r[c.dataKey]
                            ))}
                          {c.dataKey === 'modifiedAt' &&
                            formatTimestamp(r[c.dataKey])}
                          {c.dataKey !== 'status' &&
                            c.dataKey !== 'modifiedAt' &&
                            (r[c.dataKey] || '-')}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Box>
      </DialogContent>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

HLSTranscoderFilesOverwrite.propTypes = {
  title: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  rowsToOverwrite: PropTypes.array.isRequired,
  ftpServerId: PropTypes.number,
  bucketId: PropTypes.number,
};

HLSTranscoderFilesOverwrite.defaultProps = {
  ftpServerId: null,
  bucketId: null,
};

export default HLSTranscoderFilesOverwrite;
